import React, { useState, useEffect, useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Box, Button, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, IconButton, TextField, InputAdornment, MenuItem,
  Select, Chip, Avatar, Dialog, DialogTitle, DialogContent, Card,
  CardContent,
  CardActions, DialogActions, Grid, TableSortLabel, useTheme
} from "@mui/material";
import { Helmet } from "react-helmet";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DOMPurify from "dompurify";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EventIcon from '@mui/icons-material/Event';

import SectionLoader from "../components/SectionLoader";
import { useUser } from "../context/UserContext";
import Toast from "../components/Toast";
import ConfirmDialog from "../components/ConfirmDialog";
import Tooltip from '@mui/material/Tooltip';
import SettingsIcon from '@mui/icons-material/Settings';
import ModuleSettingsModal from "../components/ModuleSettingsModal";
import DraftsIcon from "@mui/icons-material/Drafts";
import DraftManager from "../utils/draftManager";
import DraftsModal from "../components/DraftsModal";
import HelpTooltip from "../components/HelpTooltip";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InventoryIcon from "@mui/icons-material/Inventory";
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import ArchiveManager from "../utils/archiveManager";
import MobileEventPreviewModal from "../components/MobileEventPreviewModal";
const statusColors = {
  Accepted: "success",
  Active: "primary",
  Upcoming: "info",
  Completed: "secondary",
  Deleted: "error",

  Pending: "warning",
  Declined: "default"
};

function StatCard({ label, count, IconComponent, active = false, onClick }) {
  return (
    <Card
      onClick={onClick}
      variant="outlined"
      sx={{
        height: "100%",
        minWidth: { xs: '100%', sm: 200 },
        width: "100%",
        borderRadius: 2.5,
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        background: active
          ? 'linear-gradient(135deg, rgba(76, 161, 175, 0.28) 0%, rgba(44, 62, 80, 0.5) 100%)'
          : 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(20px)',
        border: active
          ? '2px solid #4CA1AF'
          : '1px solid rgba(255,255,255,0.1)',
        boxShadow: active
          ? '0 8px 24px rgba(76, 161, 175, 0.35)'
          : '0 2px 8px rgba(0, 0, 0, 0.1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': onClick ? {
          transform: 'translateY(-3px)',
          borderColor: active ? '#4CA1AF' : 'rgba(76, 161, 175, 0.5)',
          background: active
            ? 'linear-gradient(135deg, rgba(76, 161, 175, 0.35) 0%, rgba(44, 62, 80, 0.6) 100%)'
            : 'rgba(76, 161, 175, 0.12)',
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
        } : {},
      }}
    >
      <CardContent sx={{ minHeight: 80, p: '16px !important' }}>
        <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
          <Box
            sx={{
              height: 48,
              width: 48,
              background: active ? '#4CA1AF' : 'rgba(76, 161, 175, 0.2)',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
              flexShrink: 0,
              transition: 'all 0.25s ease',
              boxShadow: active ? '0 4px 12px rgba(76, 161, 175, 0.4)' : 'none',
            }}
          >
            <IconComponent sx={{ fontSize: 28, color: active ? '#FFFFFF' : '#4CA1AF' }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="h5" fontWeight="bold" noWrap sx={{ color: '#FFFFFF' }}>
                {count}
              </Typography>
              {active && (
                <Box
                  sx={{
                    px: 1,
                    py: 0.2,
                    borderRadius: 1,
                    bgcolor: 'rgba(76, 161, 175, 0.3)',
                    border: '1px solid rgba(76, 161, 175, 0.6)',
                    color: '#64d2e2',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Active
                </Box>
              )}
            </Box>
            <Typography variant="body2" noWrap sx={{ color: active ? '#FFFFFF' : 'rgba(255,255,255,0.7)', fontWeight: active ? 600 : 400 }}>
              {label}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const Events = () => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const [selectedEventForView, setSelectedEventForView] = useState(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All Status");
  const [role, setRole] = useState("All Roles");
  const [selected, setSelected] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [events, setEvents] = useState([]);
  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organization, setOrganization] = useState(null); // or orgOptions[0] for default
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [editUser, setEditUser] = useState(null);
  const { user, isSwitched, effectiveOrgId } = useUser();
  const [AllEvents, SetAllEvents] = useState(0);
  const [UpcomingEvents, SetUpcomingEvents] = useState(0);
  const [ThisMonthEvents, SetThisMonthEvents] = useState(0);
  const [CompletedEvents, SetCompletedEvents] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [myGroups, setMyGroups] = useState([]);



  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [draftsOpen, setDraftsOpen] = useState(false);
  const [draftsList, setDraftsList] = useState(() => DraftManager.getDrafts('events'));
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [archivedList, setArchivedList] = useState(() => ArchiveManager.getArchivedItems('events'));
  const [filterActiveTab, setFilterActiveTab] = useState('all');
  const [mobileExampleOpen, setMobileExampleOpen] = useState(false);

  const [eventName, seteventName] = useState('');
  const [startDate, setstartDate] = useState('');
  const [contactInfo, setcontactInfo] = useState('');
  const [contactEmail, SetcontactEmail] = useState('');
  const [contactNumber, SetcontactNumber] = useState('');
  const [url, Seturl] = useState('');
  const [description, Setdescription] = useState('');

  const [origanisation, SetOriganisation] = useState('');
  const [image, Setimage] = useState({
    file: null,
    preview: '',
  });
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [group, setGroup] = useState("All Groups");

  const API_URL = process.env.REACT_APP_API_URL;

  // Auto-save draft on beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (eventName.trim() || description.trim()) {
        DraftManager.saveDraft('events', {
          eventName, startDate, contactInfo, contactEmail, contactNumber, url, description, selectedGroups
        }, activeDraftId);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [eventName, startDate, contactInfo, contactEmail, contactNumber, url, description, selectedGroups, activeDraftId]);

  const handleManualSaveDraft = () => {
    if (!eventName.trim() && !description.trim()) {
      setToast({ open: true, message: "Please enter an event name or description before saving draft.", severity: "warning" });
      return;
    }
    const res = DraftManager.saveDraft('events', {
      eventName, startDate, contactInfo, contactEmail, contactNumber, url, description, selectedGroups
    }, activeDraftId);

    if (res.success) {
      setActiveDraftId(res.draftId);
      setDraftsList(res.drafts || []);
      setToast({ open: true, message: "Event draft saved successfully!", severity: "success" });
    } else {
      setToast({ open: true, message: res.message || "Failed to save draft", severity: "error" });
    }
  };

  const handleContinueDraft = (draft) => {
    if (!draft || !draft.data) return;
    const d = draft.data;
    if (d.eventName !== undefined) seteventName(d.eventName);
    if (d.startDate !== undefined) setstartDate(d.startDate);
    if (d.contactInfo !== undefined) setcontactInfo(d.contactInfo);
    if (d.contactEmail !== undefined) SetcontactEmail(d.contactEmail);
    if (d.contactNumber !== undefined) SetcontactNumber(d.contactNumber);
    if (d.url !== undefined) Seturl(d.url);
    if (d.description !== undefined) Setdescription(d.description);
    if (d.selectedGroups !== undefined) setSelectedGroups(d.selectedGroups);
    setActiveDraftId(draft.id);
    setOpenDialog(true);
    setToast({ open: true, message: `Loaded draft: ${draft.title || 'Untitled'}`, severity: "info" });
  };

  const handleDeleteDraft = (draftId) => {
    const res = DraftManager.deleteDraft('events', draftId);
    setDraftsList(res.drafts || []);
    if (activeDraftId === draftId) setActiveDraftId(null);
    setToast({ open: true, message: "Draft removed.", severity: "info" });
  };

  const handleArchive = (eventItem) => {
    setConfirmDialog({
      open: true,
      title: "Archive Event",
      message: "Are you sure you want to archive this event? It will no longer be visible to mobile app users until restored.",
      onConfirm: () => {
        ArchiveManager.archiveItem('events', eventItem);
        setArchivedList(ArchiveManager.getArchivedItems('events'));
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        setToast({ open: true, message: "Event archived and hidden from mobile app.", severity: "info" });
      }
    });
  };

  const handleRestore = (eventId) => {
    ArchiveManager.restoreItem('events', eventId);
    setArchivedList(ArchiveManager.getArchivedItems('events'));
    setToast({ open: true, message: "Event restored to active status!", severity: "success" });
  };

  const handlePermanentDelete = (eventId) => {
    setConfirmDialog({
      open: true,
      title: "Permanently Delete Event",
      message: "Are you sure you want to permanently delete this archived event? This cannot be undone.",
      onConfirm: () => {
        ArchiveManager.deletePermanently('events', eventId);
        setArchivedList(ArchiveManager.getArchivedItems('events'));
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        setToast({ open: true, message: "Event permanently removed.", severity: "success" });
      }
    });
  };

  const handleDialogOpen = () => {
    setOpenDialog(true);
    if (user?.role === 5 && group !== "All Groups") {
      setSelectedGroups([group]);
    }
  };
  const handleDialogClose = () => {
    if (eventName.trim() || description.trim()) {
      DraftManager.saveDraft('events', {
        eventName, startDate, contactInfo, contactEmail, contactNumber, url, description, selectedGroups
      }, activeDraftId);
      setDraftsList(DraftManager.getDrafts('events'));
    }
    setOpenDialog(false);
    setSelectedGroups([]);
    seteventName('');
    setstartDate('');
    setcontactInfo('');
    SetcontactEmail('');
    SetcontactNumber('');
    Seturl('');
    Setdescription('');
    Setimage({ file: null, preview: '' });
  };
  const orgOptions = [{ id: 0, org_name: "All Organizations" }, ...organizations];


  useEffect(() => {
    const fetchUsers = async () => {

      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const storedOrgMode = sessionStorage.getItem('gpn_org_view_mode');
        const orgViewMode = storedOrgMode ? JSON.parse(storedOrgMode) : null;
        const effectiveOrgId = orgViewMode?.orgId ? orgViewMode.orgId : (organization && organization.id !== 0 ? organization.id : null);

        const response = await fetch(`${API_URL}event-list`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            search: search,
            status: status,
            role: role,
            organization: effectiveOrgId || null,
            group: group,
            page: page,
            pageSize: pageSize,
            fromDate: fromDate || undefined,
            toDate: toDate || undefined,
          })
        });
        const data = await response.json();

        if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }
        }

        setEvents(Array.isArray(data?.events) ? data.events : []);
        if (data?.pagination) {
          setTotalItems(data.pagination.totalRecords || 0);
          setTotalPages(data.pagination.totalPages || 0);
        }



      } catch (err) {
        // setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [status, role, organization, search, page, pageSize, group, fromDate, toDate]);

  useEffect(() => {
    const fetchRolesAndOrganizations = async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}roles-organizations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (!response.ok || data.success === false) {
        // 🔑 Handle invalid token
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
      }

      setRoles(data.roles);
      setOrganizations(data.organizations);

      SetAllEvents(data.allEventsCount);
      SetUpcomingEvents(data.upcomingEventsCount);
      SetThisMonthEvents(data.thisMonthEventsCount);
      SetCompletedEvents(data.completedEventsCount);

      //console.log('I am group admin', user?.role)


      if (user?.role === 5) {
        setMyGroups(data.myGroups.map(g => g.group_templates));
        setGroup(data.myGroups[0].group_id);

        // console.log('I am group admin')

      } else {
        setMyGroups(data.allGroups);
      }


    };

    fetchRolesAndOrganizations();
  }, [user?.role]);

  const handleEdit = (id) => {
    const event = events.find((event) => event.id === id);
    setEditUser(event);
    setSelectedGroups(event.groups ? event.groups.map(g => g.id) : []);
  };



  const addNewEvent = async () => {
    const token = localStorage.getItem('token');

    // Validation
    if (
      eventName === '' ||
      startDate === '' ||
      contactInfo === '' ||
      contactEmail === '' ||
      contactNumber === '' ||
      !image.file
    ) {
      setToast({ open: true, message: 'All fields are required', severity: "error" });
      return false;
    }

    const formData = new FormData();
    formData.append("event_name", eventName);
    formData.append("start_date", startDate);
    formData.append("contact_info", contactInfo);
    formData.append("contact_email", contactEmail);
    formData.append("contact_number", contactNumber);
    formData.append("url", url);
    formData.append("description", description); // Not editUser.description

    // Append image file
    formData.append("profile_pic", image.file);
    formData.append("groups", JSON.stringify(selectedGroups));

    try {
      const response = await fetch(`${API_URL}add-event`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't add Content-Type manually for FormData
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        // 🔑 Handle invalid token
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
      }

      if (response.ok) {
        if (activeDraftId) {
          DraftManager.deleteDraft('events', activeDraftId);
        }
        setToast({ open: true, message: "Event added successfully", severity: "success" });
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setToast({ open: true, message: "Add failed: " + (data?.message || 'Unknown error'), severity: "error" });
      }
    } catch (error) {
      console.error("Error while adding event:", error);
      setToast({ open: true, message: "Something went wrong!", severity: "error" });
    }
  };


  const handleSaveEdit = async () => {
    const token = localStorage.getItem('token');

    const formData = new FormData();
    formData.append("event_name", editUser.event_name);
    formData.append("start_date", editUser.start_date);
    formData.append("contact_info", editUser.contact_info);
    formData.append("contact_email", editUser.contact_email);
    formData.append("contact_number", editUser.contact_number);
    formData.append("url", editUser.url);
    formData.append("origanisation", editUser.org_id);
    formData.append("description", editUser.description);



    if (editUser.image && editUser.image instanceof File) {
      formData.append("profile_pic", editUser.image);
    }
    formData.append("groups", JSON.stringify(selectedGroups));

    formData.append("id", editUser.id);

    const response = await fetch(`${API_URL}update-event`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        // ❌ Do NOT set 'Content-Type' when sending FormData
      },
      body: formData
    });
    const data = await response.json();

    if (!response.ok || data.success === false) {
      // 🔑 Handle invalid token
      if (data.message === 'Invalid token') {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }
    }
    if (response.ok) {
      setToast({ open: true, message: "Event updated successfully", severity: "success" });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } else {
      setToast({ open: true, message: "Update failed: " + (data?.message || 'Unknown error'), severity: "error" });
    }
  };


  const handleViewDialogClose = () => {

    setEditUser(null);
    setSelectedGroups([]);

  }

  const handleDelete = async (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Event',
      message: 'Are you sure you want to delete this event? This action cannot be undone.',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });

        try {
          const token = localStorage.getItem('token');

          const response = await fetch(`${API_URL}delete-event`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ id }),
          });

          const data = await response.json();

          if (!response.ok || data.success === false) {
            // 🔑 Handle invalid token
            if (data.message === 'Invalid token') {
              localStorage.removeItem('token');
              window.location.href = '/login';
              return;
            }
          }

          if (response.ok) {
            setToast({ open: true, message: "Event deleted successfully", severity: "success" });
            setTimeout(() => {
              window.location.reload();
            }, 1500);
          } else {
            setToast({ open: true, message: "Delete failed: " + (data?.message || 'Unknown error'), severity: "error" });
          }
        } catch (error) {
          console.error('Error deleting event:', error);
          setToast({ open: true, message: "An error occurred while deleting the event", severity: "error" });
        }
      }
    });
  };



  const isThisMonth = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  };

  const [sortOrder, setSortOrder] = useState('asc');

  const safeEvents = Array.isArray(events) ? events : [];
  const safeArchivedList = Array.isArray(archivedList) ? archivedList : [];

  const displayedEvents = filterActiveTab === "archived"
    ? safeArchivedList
    : (status === 'this_month'
        ? safeEvents.filter(e => isThisMonth(e.start_date))
        : safeEvents).filter(e => !safeArchivedList.some(a => String(a.id) === String(e.id)));

  const sortedEvents = useMemo(() => {
    return [...displayedEvents].sort((a, b) => {
      if (sortOrder === 'date_asc') return new Date(a.start_date || 0) - new Date(b.start_date || 0);
      if (sortOrder === 'date_desc') return new Date(b.start_date || 0) - new Date(a.start_date || 0);
      const nameA = (a.event_name || a.name || '').trim().toLowerCase();
      const nameB = (b.event_name || b.name || '').trim().toLowerCase();
      if (sortOrder === 'desc') return nameB.localeCompare(nameA);
      return nameA.localeCompare(nameB);
    });
  }, [displayedEvents, sortOrder]);

  const handleStatCardClick = (targetStatus) => {
    setFilterActiveTab("all");
    if (status === targetStatus) {
      setStatus("All Status");
    } else {
      setStatus(targetStatus);
    }
    setPage(1);
  };

  return (
    <>
      <Helmet>
        <title>Events - GPN</title>
      </Helmet>
      <Toast toast={toast} setToast={setToast} />
      <Box>
        {/* Compact 2-Row Unified Toolbar */}
        <Paper
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            border: "1px solid #E5E7EB",
            display: "flex",
            flexDirection: "column",
            gap: 1.2,
            boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
          }}
        >
          {/* Row 1: Title, Count, Search, Sort & Main Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.2,
              pb: 1,
              borderBottom: "1px solid #F1F5F9",
            }}
          >
            {/* Left: Heading & Total */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isLight ? "#0F172A" : "#FFFFFF", fontSize: "1rem" }}>
                Events
              </Typography>
              <Chip
                size="small"
                label={`${sortedEvents.length} Total`}
                sx={{
                  bgcolor: "#E0F2FE",
                  color: "#0284C7",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  height: 22,
                }}
              />
            </Box>

            {/* Right: Search, Sort, Drafts, Add Event */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search events..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#64748B" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: "100%", sm: 200 }, "& .MuiInputBase-input": { py: 0.6, fontSize: "0.85rem" } }}
              />

              <Select
                size="small"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                displayEmpty
                renderValue={(val) => `Sort: ${val === "asc" ? "A → Z" : val === "desc" ? "Z → A" : "Date"}`}
                sx={{ width: 130, "& .MuiSelect-select": { py: 0.6, fontSize: "0.85rem" } }}
              >
                <MenuItem value="asc">Name: A → Z</MenuItem>
                <MenuItem value="desc">Name: Z → A</MenuItem>
                <MenuItem value="date_desc">Date: Newest First</MenuItem>
                <MenuItem value="date_asc">Date: Oldest First</MenuItem>
              </Select>

              <Button
                variant="outlined"
                size="small"
                startIcon={<DraftsIcon sx={{ fontSize: 18 }} />}
                onClick={() => {
                  setDraftsList(DraftManager.getDrafts('events'));
                  setDraftsOpen(true);
                }}
                sx={{
                  py: 0.5,
                  px: 1.2,
                  fontSize: "0.82rem",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  color: draftsList.length > 0 ? "#0D9488" : "#64748B",
                  borderColor: draftsList.length > 0 ? "#0D9488" : "#CBD5E1",
                  "&:hover": { borderColor: "#0D9488", bgcolor: "rgba(13, 148, 136, 0.08)" },
                }}
              >
                Drafts ({draftsList.length}/3)
              </Button>

              <Button
                variant="contained"
                size="small"
                onClick={handleDialogOpen}
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                sx={{
                  background: "#177373",
                  py: 0.6,
                  px: 1.5,
                  fontSize: "0.82rem",
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 1.5,
                  "&:hover": { background: "#115656" },
                }}
              >
                Add Event
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<PhoneIphoneIcon sx={{ fontSize: 18 }} />}
                onClick={() => setMobileExampleOpen(true)}
                sx={{
                  py: 0.5,
                  px: 1.3,
                  fontSize: "0.82rem",
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 1.5,
                  color: isLight ? "#0D9488" : "#64d2e2",
                  borderColor: isLight ? "rgba(13, 148, 136, 0.4)" : "rgba(76, 161, 175, 0.4)",
                  backgroundColor: isLight ? "rgba(13, 148, 136, 0.08)" : "rgba(76, 161, 175, 0.12)",
                  "&:hover": {
                    borderColor: isLight ? "#0D9488" : "#64d2e2",
                    bgcolor: isLight ? "rgba(13, 148, 136, 0.16)" : "rgba(76, 161, 175, 0.25)"
                  },
                }}
              >
                User Example
              </Button>
            </Box>
          </Box>

          {/* Row 2: Status Quick-Triage Chips + Filters */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {/* Status Quick Chips */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
              {[
                { key: "All Status", label: "All", count: AllEvents },
                { key: "active", label: "Upcoming", count: UpcomingEvents },
                { key: "this_month", label: "This Month", count: ThisMonthEvents },
                { key: "completed", label: "Completed", count: CompletedEvents },
              ].map((item) => {
                const isActive = filterActiveTab !== "archived" && status === item.key;
                return (
                  <Chip
                    key={item.key}
                    size="small"
                    clickable
                    label={`${item.label} (${item.count})`}
                    onClick={() => handleStatCardClick(item.key)}
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: isActive ? 700 : 500,
                      bgcolor: isActive ? "#0F766E" : "#F1F5F9",
                      color: isActive ? "#FFFFFF" : "#475569",
                      height: 26,
                      "&:hover": { bgcolor: isActive ? "#115E59" : "#E2E8F0" },
                    }}
                  />
                );
              })}

              <Chip
                size="small"
                clickable
                label={`Archived (${archivedList.length})`}
                onClick={() => {
                  setFilterActiveTab(filterActiveTab === "archived" ? "all" : "archived");
                  setPage(1);
                }}
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: filterActiveTab === "archived" ? 700 : 500,
                  bgcolor: filterActiveTab === "archived" ? "#D97706" : "#FEF3C7",
                  color: filterActiveTab === "archived" ? "#FFFFFF" : "#92400E",
                  height: 26,
                  "&:hover": { bgcolor: filterActiveTab === "archived" ? "#B45309" : "#FDE68A" },
                }}
              />
            </Box>

            {/* Filter Dropdowns: Dates, Group, Org */}
            <Box sx={{ display: "flex", gap: 0.8, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="From"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
                inputProps={{ max: "9999-12-31" }}
                sx={{ width: 130, "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" } }}
              />

              <TextField
                size="small"
                label="To"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); setPage(1); }}
                inputProps={{ max: "9999-12-31" }}
                sx={{ width: 130, "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" } }}
              />

              <Select
                size="small"
                value={group}
                onChange={(e) => { setGroup(e.target.value); setPage(1); }}
                sx={{ width: 130, "& .MuiSelect-select": { py: 0.5, fontSize: "0.8rem" } }}
              >
                <MenuItem value="All Groups">All Groups</MenuItem>
                {myGroups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </Select>

              {user?.role === 1 && !isSwitched && (
                <Autocomplete
                  size="small"
                  value={organization}
                  onChange={(event, newValue) => {
                    setOrganization(newValue);
                    setPage(1);
                  }}
                  options={orgOptions}
                  getOptionLabel={(option) => option?.org_name || ""}
                  renderInput={(params) => <TextField {...params} label="Org" sx={{ "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" } }} />}
                  sx={{ width: 170 }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}
            </Box>
          </Box>
        </Paper>

        {/* Table */}
        <Paper>


          <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>




            <Table>
              <TableHead>
                <TableRow>

                  <TableCell sx={{ fontWeight: 1000 }}>
                    <TableSortLabel
                      active={sortOrder === 'asc' || sortOrder === 'desc'}
                      direction={sortOrder === 'desc' ? 'desc' : 'asc'}
                      onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                    >
                      Event
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 1000 }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: 1000 }}>Address </TableCell>
                  {user?.role === 1 && (
                    <TableCell sx={{ fontWeight: 1000 }}>Organization</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 1000 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 1000 }}>Visibility</TableCell>
                  <TableCell sx={{ fontWeight: 1000 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>

                {loading && (
                  <div>
                    <SectionLoader open={loading} />
                  </div>
                )}

                {sortedEvents.length > 0 ? (
                  sortedEvents.map((event) => (
                    <TableRow
                      key={event.id}
                      hover
                      data-testid={`event-row-${event.id}`}
                      onClick={() => {
                        setSelectedEventForView(event);
                        setEventModalOpen(true);
                      }}
                      sx={{
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          bgcolor: isLight ? '#F0FDFA !important' : 'rgba(13, 148, 136, 0.1) !important',
                        },
                        '& td': {
                          borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.06)',
                          py: 1.5,
                          color: isLight ? '#1E293B' : '#F1F5F9',
                        }
                      }}
                    >

                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {event.image ? (
                              <Avatar
                                src={event.image}
                                alt={event.event_name}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 2,
                                  bgcolor: "#f3f4f6",
                                  color: "#2C3E50",
                                  fontWeight: 700,
                                  fontSize: 20,
                                }}
                                imgProps={{
                                  onError: (e) => {
                                    e.target.onerror = null;
                                    e.target.src = "";
                                  },
                                }}
                              >
                                {event.event_name?.[0]?.toUpperCase() || "?"}
                              </Avatar>
                            ) : (
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 2,
                                  bgcolor: "#f3f4f6",
                                  color: "#2C3E50",
                                  fontWeight: 700,
                                  fontSize: 20,
                                }}
                              >
                                {event.event_name?.[0]?.toUpperCase() || "?"}
                              </Avatar>
                            )}
                          </Box>
                          <Box>
                            <Typography fontWeight={600}>
                              {event.event_name}
                            </Typography>
                           <Typography variant="body2" color="text.secondary">
                              {event.description?.length > 50
                                ? event.description.slice(0, 50) + "..."
                                : event.description}
                            </Typography>

                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>{event.start_date}</TableCell>

                      <TableCell>{event.contact_info}</TableCell>
                      {user?.role === 1 && (
                        <TableCell>{event.origanisation?.org_name || '-'}</TableCell>
                      )}

                      <TableCell>
                        <Chip
                          label={
                            event.deleted_at !== null
                              ? "Deleted"
                              : new Date(event.start_date) > new Date()
                                ? "Upcoming"
                                : new Date(event.start_date) < new Date()
                                  ? "Completed"
                                  : "Active"
                          }
                          color={statusColors[event.deleted_at !== null ? "Deleted" : new Date(event.start_date) > new Date() ? "Upcoming" : new Date(event.start_date) < new Date() ? "Completed" : "Active"] || "default"}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />


                      </TableCell>
                      <TableCell>
                        {event.groups && event.groups.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {event.groups.map((g) => (
                              <Chip
                                key={g.id}
                                label={g.name}
                                size="small"
                                sx={{
                                  bgcolor: "#e0f2f1",
                                  color: "#00695c",
                                  fontWeight: 600
                                }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Chip
                            label="Public"
                            size="small"
                            sx={{
                              bgcolor: "#f3f4f6",
                              color: "#6b7280",
                              fontWeight: 600
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {filterActiveTab === "archived" ? (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Tooltip title="Restore Event (Show in mobile app)">
                              <IconButton
                                size="small"
                                onClick={() => handleRestore(event.id)}
                                sx={{ color: '#10B981', '&:hover': { background: 'rgba(16, 185, 129, 0.1)' } }}
                              >
                                <UnarchiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Permanently Delete">
                              <IconButton
                                size="small"
                                onClick={() => handlePermanentDelete(event.id)}
                                sx={{ color: '#EF4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                              >
                                <DeleteForeverIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : event.deleted_at ? (
                          <Typography variant="body2" color="textSecondary">Deleted</Typography>
                        ) : (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <IconButton
                              title="Edit"
                              onClick={() => handleEdit(event.id)}
                              data-testid={`edit-btn-${event.id}`}
                              sx={{ color: '#4CA1AF', '&:hover': { background: 'rgba(76, 161, 175, 0.1)' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>

                            <Tooltip title="Archive Event (Hide from mobile app)">
                              <IconButton
                                size="small"
                                onClick={() => handleArchive(event)}
                                sx={{ color: '#F59E0B', '&:hover': { background: 'rgba(245, 158, 11, 0.1)' } }}
                              >
                                <ArchiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <IconButton
                              size="small"
                              onClick={() => handleDelete(event.id)}
                              data-testid={`delete-btn-${event.id}`}
                              title="Delete"
                              sx={{ color: '#EF4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                      {filterActiveTab === "archived" ? "No archived events found" : "No Events found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>
          </TableContainer>
          
          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1, width: '100%' }}>
            <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
              {sortedEvents.map((event) => (
                <Grid item xs={12} sm={6} key={event.id}>
                  <Card
                    variant="outlined"
                    onClick={() => {
                      setSelectedEventForView(event);
                      setEventModalOpen(true);
                    }}
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'transform 0.15s ease',
                      '&:hover': { transform: 'translateY(-2px)' }
                    }}
                  >
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Avatar
                        src={event.image}
                        sx={{ width: 60, height: 60, mx: 'auto', mb: 2, borderRadius: 2 }}
                      >
                        {event.event_name?.[0]?.toUpperCase() || "?"}
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        {event.event_name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        {event.description}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="textSecondary" display="block">Date</Typography>
                          <Typography variant="body2">{event.start_date}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="textSecondary" display="block">Status</Typography>
                          <Chip 
                            label={event.deleted_at !== null ? "Deleted" : "Active"} 
                            size="small" 
                            color={event.deleted_at !== null ? "error" : "success"}
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ pt: 2, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'center', gap: 1 }}>
                        {filterActiveTab === "archived" ? (
                          <>
                            <Tooltip title="Restore Event">
                              <IconButton onClick={() => handleRestore(event.id)} sx={{ color: '#10B981' }}>
                                <UnarchiveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Permanently Delete">
                              <IconButton onClick={() => handlePermanentDelete(event.id)} sx={{ color: '#EF4444' }}>
                                <DeleteForeverIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <IconButton onClick={() => handleEdit(event.id)} sx={{ color: '#4CA1AF' }}>
                              <EditIcon />
                            </IconButton>
                            <Tooltip title="Archive Event">
                              <IconButton onClick={() => handleArchive(event)} sx={{ color: '#F59E0B' }}>
                                <ArchiveIcon />
                              </IconButton>
                            </Tooltip>
                            <IconButton onClick={() => handleDelete(event.id)} sx={{ color: '#EF4444' }}>
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              {events.length === 0 && (
                <Grid item xs={12}>
                  <Typography align="center" sx={{ py: 4, color: 'textSecondary' }}>No Events found</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
          {/* Pagination */}
          {totalItems > 0 && (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
                <Typography variant="body2">
                  Showing {page * pageSize - pageSize + 1}-{Math.min(page * pageSize, totalItems)} of {totalItems}
                </Typography>
                <Box>
                  <Button variant="outlined" sx={{ minWidth: 40, mx: 1 }} onClick={() => setPage(page - 1)} disabled={page === 1}>{'<'}</Button>
                  <Button variant="contained" sx={{ minWidth: 40, mx: 1, background: '#177373' }} onClick={() => setPage(page)}>{page}</Button>
                  <Button variant="outlined" sx={{ minWidth: 40, mx: 1 }} onClick={() => setPage(page + 1)} disabled={page === totalPages}>{'>'}</Button>
                </Box>
              </Box>

            </>

          )}
        </Paper>
        {editUser && (
          <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Event</DialogTitle>
            <DialogContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <TextField
                    label="Event"
                    value={editUser.event_name}
                    onChange={(e) =>
                      setEditUser({ ...editUser, event_name: e.target.value })
                    }
                    data-testid="edit-event-name-input"
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <TextField
                    label="Start Date"
                    type="datetime-local"
                    value={editUser.start_date}
                    onChange={(e) =>
                      setEditUser({
                        ...editUser,
                        start_date: e.target.value,
                      })
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <TextField label="Description" multiline
                    rows={4} value={editUser.description}
                    onChange={(e) => setEditUser({ ...editUser, description: e.target.value })} />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <TextField label="Contact Email" value={editUser.contact_email}
                    onChange={(e) => setEditUser({ ...editUser, contact_email: e.target.value })} />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <TextField label="Contact Number" value={editUser.contact_number}
                    onChange={(e) => setEditUser({ ...editUser, contact_number: e.target.value })} />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <TextField label="Address" value={editUser.contact_info}
                    onChange={(e) => setEditUser({ ...editUser, contact_info: e.target.value })} />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <TextField label="URL" value={editUser.url}
                    onChange={(e) => setEditUser({ ...editUser, url: e.target.value })} />
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Image</Typography>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setEditUser((prev) => ({
                          ...prev,
                          image: file,
                          imagePreview: URL.createObjectURL(file),
                        }));
                      }
                    }}
                  />

                  {/* Show uploaded preview or existing profile pic */}
                  {editUser.imagePreview ? (
                    <Box sx={{ mt: 1 }}>
                      <img
                        src={editUser.imagePreview}
                        alt="Preview"
                        style={{ maxWidth: 80, maxHeight: 80, borderRadius: 4 }}
                      />
                    </Box>
                  ) : (
                    editUser.image && typeof editUser.image === 'string' && (
                      <Box sx={{ mt: 1 }}>
                        <img
                          src={editUser.image}
                          alt="Current"
                          style={{ maxWidth: 80, maxHeight: 80, borderRadius: 4 }}
                        />
                      </Box>
                    )
                  )}
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <Autocomplete
                    multiple
                    options={myGroups || []}
                    getOptionLabel={(option) => option.name || ''}
                    value={(myGroups || []).filter(g => selectedGroups.includes(g.id))}
                    onChange={(event, newValue) => {
                      setSelectedGroups(newValue.map(v => v.id));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Visibility Groups" placeholder="Select Groups" />
                    )}
                  />
                </Box>


                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}></Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button onClick={handleViewDialogClose}>Cancel</Button>

                <Button variant="contained" onClick={handleSaveEdit} sx={{ background: '#177373' }}>Save</Button>
              </Box>
            </DialogContent>
          </Dialog>
        )}









        <Dialog
          open={openDialog}
          onClose={handleDialogClose}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 3,
              bgcolor: isLight ? '#FFFFFF' : '#141E2C',
              backgroundImage: isLight
                ? 'none'
                : 'linear-gradient(145deg, #182232 0%, #0F172A 100%)',
              border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(76, 161, 175, 0.25)',
              boxShadow: isLight
                ? '0 20px 45px rgba(0,0,0,0.1)'
                : '0 24px 60px rgba(0,0,0,0.6)',
              overflow: 'hidden',
            }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 3,
              py: 2,
              borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
              bgcolor: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.02)',
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: isLight ? 'rgba(13, 148, 136, 0.12)' : 'rgba(76, 161, 175, 0.2)',
                  color: isLight ? '#0D9488' : '#4CA1AF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: isLight ? '1px solid rgba(13, 148, 136, 0.2)' : '1px solid rgba(76, 161, 175, 0.35)',
                }}
              >
                <EventIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: isLight ? '#0F172A' : '#FFFFFF', lineHeight: 1.2 }}>
                  Add Event
                </Typography>
                <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.6)' }}>
                  Create and publish a church or ministry event
                </Typography>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PhoneIphoneIcon sx={{ fontSize: 16 }} />}
                onClick={() => setMobileExampleOpen(true)}
                sx={{
                  color: '#64d2e2',
                  borderColor: 'rgba(76, 161, 175, 0.4)',
                  bgcolor: 'rgba(76, 161, 175, 0.1)',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  borderRadius: 1.5,
                  '&:hover': {
                    bgcolor: 'rgba(76, 161, 175, 0.25)',
                    borderColor: '#4CA1AF',
                  },
                }}
              >
                User Example
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleManualSaveDraft}
                sx={{
                  color: '#64d2e2',
                  borderColor: 'rgba(76, 161, 175, 0.4)',
                  bgcolor: 'rgba(76, 161, 175, 0.1)',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  borderRadius: 1.5,
                  '&:hover': {
                    bgcolor: 'rgba(76, 161, 175, 0.25)',
                    borderColor: '#4CA1AF',
                  },
                }}
              >
                Save Draft
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<DraftsIcon sx={{ fontSize: 16 }} />}
                onClick={() => {
                  setDraftsList(DraftManager.getDrafts('events'));
                  setDraftsOpen(true);
                }}
                sx={{
                  bgcolor: 'rgba(76, 161, 175, 0.3)',
                  border: '1px solid rgba(76, 161, 175, 0.6)',
                  color: '#fff',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  borderRadius: 1.5,
                  '&:hover': {
                    bgcolor: 'rgba(76, 161, 175, 0.5)',
                  },
                }}
              >
                Drafts ({draftsList.length}/3)
              </Button>
              <IconButton
                onClick={handleDialogClose}
                size="small"
                sx={{
                  color: isLight ? '#64748B' : 'rgba(255,255,255,0.7)',
                  '&:hover': {
                    color: isLight ? '#0F172A' : '#FFFFFF',
                    bgcolor: isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>

              {/* Section 1: Event Name & Date */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  bgcolor: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.03)',
                  border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.07)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1.2fr 0.8fr' },
                    gap: 2,
                  }}
                >
                  <Box>
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ color: isLight ? '#1E293B' : '#E2E8F0' }}>
                        Event Name <span style={{ color: '#EF4444' }}>*</span>
                      </Typography>
                      <HelpTooltip
                        title="Event Name"
                        purpose="Headline name of the church or community event."
                        howItWorks="Type the title of the event."
                        mobileEffect="Displayed in the events calendar and notifications in the mobile app."
                      />
                    </Box>
                    <TextField
                      fullWidth
                      placeholder="e.g. Annual Youth Conference 2026"
                      value={eventName}
                      onChange={(e) => seteventName(e.target.value)}
                      data-testid="event-name-input"
                      size="small"
                    />
                  </Box>

                  <Box>
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ color: isLight ? '#1E293B' : '#E2E8F0' }}>
                        Start Date & Time <span style={{ color: '#EF4444' }}>*</span>
                      </Typography>
                      <HelpTooltip
                        title="Event Date and Time"
                        purpose="When the event will occur."
                        howItWorks="Choose start date and time."
                        mobileEffect="Users can add to their mobile calendar and receive reminders."
                      />
                    </Box>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      value={startDate}
                      onChange={(e) => setstartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      data-testid="event-startdate-input"
                      size="small"
                    />
                  </Box>
                </Box>

                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: isLight ? '#1E293B' : '#E2E8F0' }}>
                      Description
                    </Typography>
                    <HelpTooltip
                      title="Event Description"
                      purpose="Details about speakers, itinerary, and venue."
                      howItWorks="Explain what attendees should expect."
                      mobileEffect="Shown on the event details page in the app."
                    />
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Enter details, schedule, theme, or guest speakers..."
                    value={description}
                    onChange={(e) => Setdescription(e.target.value)}
                    data-testid="event-description-input"
                  />
                </Box>
              </Box>

              {/* Section 2: Banner Image & Visibility */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  bgcolor: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.03)',
                  border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.07)',
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                  alignItems: 'start',
                }}
              >
                {/* Banner Image */}
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: isLight ? '#1E293B' : '#E2E8F0' }}>
                      Banner Image <span style={{ color: '#EF4444' }}>*</span>
                    </Typography>
                    <HelpTooltip
                      title="Event Banner"
                      purpose="Promotional banner artwork for the event."
                      howItWorks="Upload a landscape image file."
                      mobileEffect="Displayed on event cards and headers."
                    />
                  </Box>

                  <Box
                    component="label"
                    sx={{
                      border: '1.5px dashed',
                      borderColor: image.preview ? (isLight ? '#0D9488' : '#4CA1AF') : (isLight ? '#CBD5E1' : 'rgba(255,255,255,0.2)'),
                      borderRadius: 2,
                      p: 1.5,
                      bgcolor: isLight ? '#FFFFFF' : 'rgba(15, 23, 42, 0.6)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      minHeight: '120px',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: isLight ? '#0D9488' : '#4CA1AF',
                        bgcolor: isLight ? 'rgba(13, 148, 136, 0.04)' : 'rgba(76, 161, 175, 0.08)',
                      },
                      position: 'relative',
                    }}
                  >
                    <input
                      data-testid="event-image-input"
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          Setimage({
                            file: file,
                            preview: URL.createObjectURL(file),
                          });
                        }
                      }}
                    />
                    {image.preview ? (
                      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          component="img"
                          src={image.preview}
                          alt="Banner preview"
                          sx={{
                            width: 80,
                            height: 56,
                            objectFit: 'cover',
                            borderRadius: 1.5,
                            border: '1px solid rgba(76, 161, 175, 0.4)',
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                            {image.file?.name || 'banner-image.jpg'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: isLight ? '#0D9488' : '#4CA1AF', fontWeight: 600 }}>
                            ✓ Click to change image
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 1 }}>
                        <CloudUploadIcon sx={{ fontSize: 32, color: isLight ? '#0D9488' : '#4CA1AF', mb: 0.5 }} />
                        <Typography variant="body2" fontWeight={600} sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                          Upload Event Banner
                        </Typography>
                        <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.6)', display: 'block' }}>
                          Recommended: Landscape JPG or PNG
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Visibility Groups */}
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: isLight ? '#1E293B' : '#E2E8F0' }}>
                      Visibility Groups
                    </Typography>
                    <HelpTooltip
                      title="Visibility Groups"
                      purpose="Restrict event visibility to specific prayer or ministry groups."
                      howItWorks="Leave empty for public, or pick one or more groups."
                      mobileEffect="Only members of selected groups will see this event."
                    />
                  </Box>
                  <Autocomplete
                    multiple
                    size="small"
                    options={myGroups || []}
                    getOptionLabel={(option) => option.name || ''}
                    value={(myGroups || []).filter(g => selectedGroups.includes(g.id))}
                    onChange={(event, newValue) => {
                      setSelectedGroups(newValue.map(v => v.id));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Public (all church members)" size="small" />
                    )}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => (
                        <Chip
                          label={option.name}
                          size="small"
                          {...getTagProps({ index })}
                          sx={{
                            bgcolor: isLight ? 'rgba(13, 148, 136, 0.15)' : 'rgba(76, 161, 175, 0.25)',
                            color: isLight ? '#0F766E' : '#64d2e2',
                            fontWeight: 600,
                          }}
                        />
                      ))
                    }
                  />
                  <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.5)', display: 'block', mt: 1 }}>
                    Leave empty to make this event visible to all church members.
                  </Typography>
                </Box>
              </Box>

              {/* Section 3: Location & Organizer Contacts */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  bgcolor: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.03)',
                  border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.07)',
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                {/* Location / Address */}
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: isLight ? '#1E293B' : '#E2E8F0' }}>
                      Location / Address
                    </Typography>
                    <HelpTooltip
                      title="Event Location"
                      purpose="Physical venue or online meeting link."
                      howItWorks="Enter venue address or directions."
                      mobileEffect="Provides map directions or meeting links in the app."
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. 123 Faith Ave, Main Sanctuary"
                    value={contactInfo}
                    onChange={(e) => setcontactInfo(e.target.value)}
                    data-testid="event-address-input"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon sx={{ fontSize: 18, color: isLight ? '#0D9488' : '#4CA1AF' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* URL / Live Stream */}
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: isLight ? '#1E293B' : '#E2E8F0' }}>
                      URL / Live Stream
                    </Typography>
                    <HelpTooltip
                      title="Registration / Livestream Link"
                      purpose="External link for ticketing, website, or live stream."
                      howItWorks="Paste full URL starting with https://."
                      mobileEffect="Renders as a direct action button on mobile."
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="https://example.org/event"
                    value={url}
                    onChange={(e) => Seturl(e.target.value)}
                    data-testid="event-url-input"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkIcon sx={{ fontSize: 18, color: isLight ? '#0D9488' : '#4CA1AF' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* Contact Email */}
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: isLight ? '#1E293B' : '#E2E8F0' }}>
                      Contact Email
                    </Typography>
                    <HelpTooltip
                      title="Contact Email"
                      purpose="Email address for attendee questions or registration."
                      howItWorks="Enter the event organizer's email."
                      mobileEffect="Users can tap to send an email."
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="contact@church.org"
                    value={contactEmail}
                    onChange={(e) => SetcontactEmail(e.target.value)}
                    data-testid="event-contact-email-input"
                  />
                </Box>

                {/* Contact Number */}
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.75}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: isLight ? '#1E293B' : '#E2E8F0' }}>
                      Contact Number
                    </Typography>
                    <HelpTooltip
                      title="Contact Number"
                      purpose="Phone number for direct questions or directions."
                      howItWorks="Enter phone with country code if needed."
                      mobileEffect="Users can tap to call directly from the app."
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="+1 (555) 000-0000"
                    value={contactNumber}
                    onChange={(e) => SetcontactNumber(e.target.value)}
                    data-testid="event-contact-number-input"
                  />
                </Box>
              </Box>

            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
              bgcolor: isLight ? '#F8FAFC' : 'rgba(15, 23, 42, 0.4)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.5)' }}>
              Fields marked with <span style={{ color: '#EF4444' }}>*</span> are required
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                onClick={handleDialogClose}
                variant="outlined"
                sx={{
                  borderColor: isLight ? '#CBD5E1' : 'rgba(255, 255, 255, 0.2)',
                  color: isLight ? '#475569' : '#CBD5E1',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2.5,
                  '&:hover': {
                    borderColor: isLight ? '#94A3B8' : 'rgba(255, 255, 255, 0.4)',
                    bgcolor: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={addNewEvent}
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 3.5,
                  boxShadow: '0 4px 14px rgba(13, 148, 136, 0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2DD4BF 0%, #0F766E 100%)',
                    boxShadow: '0 6px 20px rgba(13, 148, 136, 0.5)',
                  }
                }}
              >
                Submit Event
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Drafts Modal */}
        <DraftsModal
          open={draftsOpen}
          onClose={() => setDraftsOpen(false)}
          moduleName="Events"
          drafts={draftsList}
          onContinueEditing={handleContinueDraft}
          onDeleteDraft={handleDeleteDraft}
        />

        {/* Event Detail Modal Popup */}
        <Dialog
          open={eventModalOpen}
          onClose={() => {
            setEventModalOpen(false);
            setSelectedEventForView(null);
          }}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 3,
              bgcolor: isLight ? '#FFFFFF' : '#1e293b',
              backgroundImage: 'none',
              overflow: 'hidden',
            }
          }}
        >
          {selectedEventForView && (() => {
            const ev = selectedEventForView;
            const evStatus = ev.deleted_at !== null
              ? "Deleted"
              : new Date(ev.start_date) > new Date()
                ? "Upcoming"
                : new Date(ev.start_date) < new Date()
                  ? "Completed"
                  : "Active";

            return (
              <>
                {/* Header Banner */}
                <Box
                  sx={{
                    position: 'relative',
                    p: 3,
                    background: isLight
                      ? 'linear-gradient(135deg, #0F766E 0%, #0D9488 100%)'
                      : 'linear-gradient(135deg, #115E59 0%, #134E4A 100%)',
                    color: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Box display="flex" gap={2} alignItems="center">
                    <Avatar
                      src={ev.image || undefined}
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 2.5,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: '#FFFFFF',
                        fontWeight: 800,
                        fontSize: 26,
                        border: '2px solid rgba(255,255,255,0.4)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    >
                      {ev.event_name?.[0]?.toUpperCase() || <EventIcon sx={{ fontSize: 32 }} />}
                    </Avatar>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Chip
                          label={evStatus}
                          size="small"
                          sx={{
                            bgcolor: evStatus === 'Upcoming' ? '#3B82F6' : evStatus === 'Active' ? '#10B981' : evStatus === 'Completed' ? '#8B5CF6' : '#EF4444',
                            color: '#FFFFFF',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            height: 22,
                          }}
                        />
                        {ev.groups && ev.groups.length > 0 ? (
                          ev.groups.map(g => (
                            <Chip
                              key={g.id}
                              label={g.name}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.25)',
                                color: '#FFFFFF',
                                fontWeight: 600,
                                fontSize: '0.72rem',
                                height: 22,
                              }}
                            />
                          ))
                        ) : (
                          <Chip
                            label="Public Event"
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.2)',
                              color: '#FFFFFF',
                              fontWeight: 600,
                              fontSize: '0.72rem',
                              height: 22,
                            }}
                          />
                        )}
                        {ev.origanisation?.org_name && (
                          <Chip
                            label={ev.origanisation.org_name}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.15)',
                              color: '#FFFFFF',
                              fontWeight: 600,
                              fontSize: '0.72rem',
                              height: 22,
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="h5" fontWeight={800} sx={{ mt: 0.8, color: '#FFFFFF' }}>
                        {ev.event_name}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    onClick={() => {
                      setEventModalOpen(false);
                      setSelectedEventForView(null);
                    }}
                    sx={{ color: '#FFFFFF', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>

                <DialogContent sx={{ p: 3, maxHeight: '65vh', overflowY: 'auto' }}>
                  <Box display="flex" flexDirection="column" gap={2.5}>
                    {/* Event Banner Image if present */}
                    {ev.image && (
                      <Box
                        sx={{
                          width: '100%',
                          maxHeight: 280,
                          borderRadius: 2.5,
                          overflow: 'hidden',
                          bgcolor: '#000',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.1)'
                        }}
                      >
                        <img
                          src={ev.image}
                          alt={ev.event_name}
                          style={{ width: '100%', maxHeight: 280, objectFit: 'cover' }}
                        />
                      </Box>
                    )}

                    {/* Schedule and Location Info */}
                    <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: isLight ? '#F8FAFC' : 'rgba(255,255,255,0.02)',
                          borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.08)',
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <CalendarTodayIcon sx={{ fontSize: 18, color: '#0D9488' }} />
                          <Typography variant="caption" fontWeight={800} sx={{ color: isLight ? '#64748B' : '#94A3B8', textTransform: 'uppercase' }}>
                            Schedule & Timing
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>Start:</strong> {ev.start_date ? new Date(ev.start_date).toLocaleString() : 'N/A'}
                        </Typography>
                        {ev.end_date && (
                          <Typography variant="body2" sx={{ mt: 0.3 }}>
                            <strong>End:</strong> {new Date(ev.end_date).toLocaleString()}
                          </Typography>
                        )}
                      </Paper>

                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: isLight ? '#F8FAFC' : 'rgba(255,255,255,0.02)',
                          borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.08)',
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <LocationOnIcon sx={{ fontSize: 18, color: '#0D9488' }} />
                          <Typography variant="caption" fontWeight={800} sx={{ color: isLight ? '#64748B' : '#94A3B8', textTransform: 'uppercase' }}>
                            Location & Venue
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {ev.address || ev.location || 'Location details not specified.'}
                        </Typography>
                      </Paper>
                    </Box>

                    {/* Description Section */}
                    <Box>
                      <Typography variant="caption" fontWeight={800} sx={{ color: isLight ? '#64748B' : '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                        Event Description
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          bgcolor: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.02)',
                          borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.08)',
                          minHeight: 100,
                        }}
                      >
                        <Box
                          sx={{
                            color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.88)',
                            lineHeight: 1.7,
                            fontSize: '0.95rem',
                            '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
                            '& strong, & b': { color: isLight ? '#0F172A' : '#FFFFFF', fontWeight: 700 },
                          }}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(ev.description || 'No description provided for this event.')
                          }}
                        />
                      </Paper>
                    </Box>
                  </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2, px: 3, borderTop: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.08)', gap: 1 }}>
                  {!ev.deleted_at && (
                    <Button
                      onClick={() => {
                        setEventModalOpen(false);
                        handleEdit(ev.id);
                      }}
                      variant="contained"
                      startIcon={<EditIcon />}
                      size="small"
                      sx={{
                        bgcolor: '#0D9488',
                        '&:hover': { bgcolor: '#0F766E' },
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 2
                      }}
                    >
                      Edit Event
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      setEventModalOpen(false);
                      setSelectedEventForView(null);
                    }}
                    variant="outlined"
                    color="inherit"
                    size="small"
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    Close
                  </Button>
                </DialogActions>
              </>
            );
          })()}
        </Dialog>

      </Box>
      <Toast toast={toast} setToast={setToast} />
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        severity="error"
      />
      {/* Floating Settings Icon */}
      <Tooltip title="Events Settings" placement="left">
        <IconButton
          onClick={() => setSettingsOpen(true)}
          sx={{
            position: 'fixed', bottom: 32, right: 32,
            bgcolor: 'rgba(76, 161, 175, 0.15)', border: '1px solid rgba(76, 161, 175, 0.4)',
            color: '#64d2e2', width: 48, height: 48, zIndex: 1300, backdropFilter: 'blur(8px)',
            '&:hover': { bgcolor: 'rgba(76, 161, 175, 0.3)', transform: 'rotate(20deg)' },
            transition: 'all 0.25s ease',
          }}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <ModuleSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} moduleKey="events" moduleTitle="Events" />
      <MobileEventPreviewModal
        open={mobileExampleOpen}
        onClose={() => setMobileExampleOpen(false)}
        isExample={true}
        liveEvent={{ eventName, startDate, description, contactInfo, contactEmail, contactNumber, url, image }}
        churchName={organization?.org_name || user?.organization || 'Way Truth Life Church'}
      />
    </>
  );
};

export default Events;